"use client";

import React, { useState } from "react";
import { ArrowLeft, ArrowRight, Bot, Clock, Users, CircleCheck as CheckCircle2, Target, Cpu, CircleAlert as AlertCircle, Quote, Sparkles, Layers, Wrench, Timer, ShieldAlert, Flag, RotateCcw, Compass, Trophy, Play, Presentation, Laptop, Check, Award } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProgramContextConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading || !posts || posts.length === 0) {
    return null;
  }
  return <div className="sr-only" aria-hidden="true">
      {posts.map((item, idx) => <span key={item.id} data-index={idx}>
          {item.title}
        </span>)}
    </div>;
}
export default function RoboticsCourseHP6Detail() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const wp_query_params = {
    post_type: "program",
    per_page: 3
  };
  const wp_query = new WP_Query(wp_query_params);
  const t = {
    vi: {
      // 1. Breadcrumb
      breadcrumbHome: "Trang chủ",
      breadcrumbPathway: "Lộ trình Robotics",
      breadcrumbTrack: "Robotics 7+ tuổi",
      breadcrumbCurrent: "HP6 – Robot nhiệm vụ",
      // 2. Hero & Quick Specs
      courseCode: "HP6",
      courseName: "Robot nhiệm vụ",
      tagline: "Chiến thuật, cơ chế chấp hành và đấu trường giải quyết nhiệm vụ tự hành.",
      age: "Từ 7 tuổi",
      platform: "WhalesBot AI Module 1S",
      duration: "12 buổi × 90 phút",
      groupSize: "2–3 học sinh/bộ kit",
      prerequisite: "Hoàn thành HP5",
      quote: "Thiết kế chiến lược và giải quyết nhiệm vụ như một kỹ sư Robotics.",
      btnPathway: "XEM TOÀN BỘ LỘ TRÌNH",
      btnConsult: "ĐĂNG KÝ TƯ VẤN",
      specsTitle: "Thông số khóa học",
      specsStandardKit: "Bộ kit tiêu chuẩn:",
      specsKitDesc: "WhalesBot AI Module 1S (Cảm biến AI, động cơ servo, cơ cấu module)",
      // 3. Who is this course for
      whoTitle: "Khóa học này dành cho ai?",
      whoLead: "Dành cho học viên đã hoàn thành HP5. HP6 là học phần đấu trường mô phỏng, rèn chiến thuật và cơ cấu chấp hành nhiệm vụ.",
      whoPoints: [{
        title: "Đã hoàn thành khóa học HP5",
        desc: "Học sinh đã làm chủ nền tảng thị giác máy tính, nhận diện biển báo và điều khiển tự hành từ HP5."
      }, {
        title: "Khóa học chiến thuật chốt (Capstone Mission-Strategy)",
        desc: "Là chặng cao nhất trong tuyến đào tạo cốt lõi 7+ tuổi, nơi hội tụ toàn bộ kỹ năng cơ khí, thuật toán và phân bổ thời gian thực chiến."
      }, {
        title: "Chuyển tiếp sang phân nhánh Enjoy AI riêng biệt (Không phải 'HP7')",
        desc: "Sau HP6, học sinh có định hướng thi đấu sẽ chuyển tiếp sang tuyến luyện thi chuyên biệt Enjoy AI chứ không có cấp độ HP7 theo tuyến truyền thống."
      }],
      // 4. 12-session timeline grouped into 6 stages
      timelineTitle: "Cấu trúc 12 buổi học (6 giai đoạn)",
      timelineDesc: "Lộ trình 12 buổi được nhóm thành 6 giai đoạn kỹ thuật, xây dựng từ 11 chủ đề nhiệm vụ chính thức của HP6.",
      timelineStages: [{
        stageNum: "01",
        stageLabel: "Giai đoạn 1",
        sessions: "Buổi 1 – 2",
        title: "Cơ chế đấu trường & Cơ cấu chấp hành",
        topicsCovered: "Cơ cấu chấp hành nhiệm vụ (Mission Mechanisms)",
        desc: "Làm quen sa bàn nhiệm vụ chuẩn, phân tích điểm số mục tiêu và thiết kế các cơ cấu đầu cuối chuyên biệt cho thao tác cơ học."
      }, {
        stageNum: "02",
        stageLabel: "Giai đoạn 2",
        sessions: "Buổi 3 – 4",
        title: "Tối ưu hóa động cơ & Tương tác 3D",
        topicsCovered: "1 Động cơ + Cơ cấu bị động & Đẩy / Nâng / Thả / Bắn",
        desc: "Tận dụng đòn bẩy, trọng lực, bánh cóc để nhân đôi năng lực từ 1 motor; làm chủ 4 thao tác vật lý cốt lõi tương tác đạo cụ."
      }, {
        stageNum: "03",
        stageLabel: "Giai đoạn 3",
        sessions: "Buổi 5 – 6",
        title: "Căn chỉnh định vị & Hàm nhiệm vụ",
        topicsCovered: "Căn chỉnh định vị (Alignment) & Hàm thực thi nhiệm vụ (Mission Functions)",
        desc: "Kỹ thuật hiệu chuẩn góc và bám biên reset tọa độ triệt tiêu sai số; đóng gói module chuỗi hành động thành hàm lập trình tái sử dụng."
      }, {
        stageNum: "04",
        stageLabel: "Giai đoạn 4",
        sessions: "Buổi 7 – 8",
        title: "Biến trạng thái & Chiến thuật lộ trình",
        topicsCovered: "Biến trạng thái (State Variables) & Chiến thuật lộ trình (Strategic Routing)",
        desc: "Quản trị cờ hiệu logic ghi nhớ tiến trình; lập kế hoạch phân bổ thời gian và hoạch định đường đi gom điểm ưu tiên cao nhất."
      }, {
        stageNum: "05",
        stageLabel: "Giai đoạn 5",
        sessions: "Buổi 9 – 10",
        title: "Thử thách 150 giây & Độ ổn định lặp lại",
        topicsCovered: "Thử thách 150 giây (150-second Mission) & Độ ổn định & Lặp lại (Repeatability)",
        desc: "Vận hành hoàn toàn tự động trong khung giờ chuẩn 150s; thống kê tỷ lệ thành công qua hàng chục lượt chạy thử nghiệm."
      }, {
        stageNum: "06",
        stageLabel: "Giai đoạn 6",
        sessions: "Buổi 11 – 12",
        title: "Nhiệm vụ bí mật & Đấu trường mô phỏng",
        topicsCovered: "Nhiệm vụ bí mật (Secret Mission Drill) & Đấu trường mô phỏng (Simulated Tournament)",
        desc: "Rèn luyện phản xạ kỹ thuật khi đạo cụ thay đổi bất ngờ trước giờ chạy; thi đấu đối kháng áp lực thời gian và bảo vệ chiến thuật."
      }],
      // 11 Core Topics
      topicsHeading: "11 Trọng tâm chuyên môn của HP6",
      topicsSub: "Tập trung vào cơ chế vật lý thông minh, tối ưu 1 động cơ với cơ cấu bị động và thuật toán tự hành 150 giây.",
      topics: [{
        title: "Cơ cấu chấp hành nhiệm vụ",
        desc: "Thiết kế các bộ phận thao tác chuyên biệt cho từng chướng ngại vật trên sa bàn.",
        icon: Wrench
      }, {
        title: "1 Động cơ + Cơ cấu bị động",
        desc: "Tận dụng đòn bẩy, trọng lực, bánh cóc và liên kết cơ học để nhân đôi năng lực thao tác từ 1 motor duy nhất.",
        icon: Cpu
      }, {
        title: "Đẩy, nâng, thả, bắn (Push, Lift, Drop, Launch)",
        desc: "Làm chủ 4 thao tác vật lý cốt lõi tương tác với đạo cụ nhiệm vụ trong không gian 3 chiều.",
        icon: Layers
      }, {
        title: "Căn chỉnh định vị (Alignment)",
        desc: "Kỹ thuật hiệu chuẩn góc và khoảng cách bám biên, reset tọa độ để hạn chế tích lũy sai số.",
        icon: Target
      }, {
        title: "Hàm thực thi nhiệm vụ (Mission Functions)",
        desc: "Module hóa các chuỗi hành động thành hàm lập trình tái sử dụng giúp mã nguồn ngắn gọn, ổn định.",
        icon: Bot
      }, {
        title: "Biến trạng thái (State Variables)",
        desc: "Quản lý luồng xử lý và ghi nhớ tiến trình nhiệm vụ bằng các biến cờ hiệu logic.",
        icon: Sparkles
      }, {
        title: "Chiến thuật lộ trình (Strategic Routing)",
        desc: "Lập kế hoạch phân bổ thời gian, tính điểm ưu tiên và lộ trình di chuyển tối ưu trên sa bàn.",
        icon: Flag
      }, {
        title: "Thử thách 150 giây (150-second Mission)",
        desc: "Vận hành hoàn toàn tự động, giải quyết tối đa số lượng mục tiêu trong khung thời gian chuẩn 150s.",
        icon: Timer
      }, {
        title: "Độ ổn định & Lặp lại (Repeatability)",
        desc: "Đo lường tỉ lệ thành công lặp lại qua hàng chục lượt chạy thử nghiệm có kiểm soát.",
        icon: CheckCircle2
      }, {
        title: "Nhiệm vụ bí mật (Secret Mission Drill)",
        desc: "Rèn luyện phản xạ kỹ thuật khi ban tổ chức thay đổi thông số đạo cụ ngay trước giờ thi đấu.",
        icon: ShieldAlert
      }, {
        title: "Đấu trường mô phỏng (Simulated Tournament)",
        desc: "Trải nghiệm áp lực thi đấu đối kháng điểm số, quản lý sự cố và tinh thần đồng đội trên sân thi.",
        icon: RotateCcw
      }],
      // Format Note Alert
      noticeTitle: "Lưu ý về thể thức & Kỳ thi Enjoy AI",
      noticeBody: "Giai đoạn này áp dụng thể thức thi đấu mô phỏng Invention Trials với nhiệm vụ tự hành trong 150 giây để rèn luyện tư duy thực chiến. Đây là mô hình huấn luyện giả lập, không phải là kỳ thi chính thức Enjoy AI và việc hoàn thành HP6 không đảm bảo tư cách đạt chuẩn kỳ thi.",
      // 5. 4-step Learning Method
      methodTitle: "Phương pháp học tập 4 bước",
      methodDesc: "Quy trình kỹ thuật chuẩn hóa rèn luyện tư duy đấu trường và giải quyết nhiệm vụ trong mỗi buổi học.",
      methodSteps: [{
        step: "01",
        name: "Khởi động & Phân tích nhiệm vụ",
        desc: "Nghiên cứu sa bàn, phân tích luật thi đấu, xác định mục tiêu điểm số và tính toán quỹ thời gian 150 giây.",
        icon: Play
      }, {
        step: "02",
        name: "Chế tạo cơ cấu chấp hành",
        desc: "Lắp ráp khung gầm, tối ưu hóa 1 động cơ với cơ cấu bị động và hoàn thiện các tay thao tác đẩy/nâng/thả/bắn.",
        icon: Wrench
      }, {
        step: "03",
        name: "Lập trình & Căn chỉnh chiến thuật",
        desc: "Viết hàm nhiệm vụ mô-đun, quản lý biến trạng thái, hiệu chuẩn góc bám biên và lập trình thuật toán chạy tự hành.",
        icon: Target
      }, {
        step: "04",
        name: "Chạy thử đối kháng & Đánh giá",
        desc: "Vận hành thử nghiệm tính giờ, đối chiếu tỷ lệ ổn định lặp lại, xử lý sự cố tại chỗ và bảo vệ phương án kỹ thuật.",
        icon: Presentation
      }],
      // 6. Equipment Card
      equipTitle: "Trang thiết bị & Yêu cầu phần cứng",
      equipSubtitle: "Mô hình trang thiết bị chuẩn kỹ thuật cho học phần HP6",
      equipKitLabel: "Bộ kit phần cứng:",
      equipKitName: "WhalesBot AI Module 1S",
      equipKitDesc: "Tích hợp bộ vi xử lý AI, cảm biến thị giác, động cơ servo độ chính xác cao và hệ linh kiện cơ cấu đòn bẩy.",
      equipCostLabel: "Chi phí thiết bị tham khảo (không phải học phí):",
      equipCostValue: "~6.500.000 VNĐ / bộ kit",
      equipComputerLabel: "Yêu cầu máy tính:",
      equipComputerValue: "Bắt buộc có máy tính (Laptop) để lập trình khối lệnh nâng cao và nạp thuật toán tự hành.",
      equipNote: "Lưu ý: Mức giá kit trên là chi phí thiết bị tham khảo trên thị trường phần cứng, không bao gồm học phí đào tạo.",
      // 7. Small-group learning model
      groupModelTitle: "Mô hình nhóm học tập nhỏ",
      groupModelLead: "Tối ưu hóa thời gian thao tác phần cứng và cọ xát chiến thuật thực chiến trên sa bàn.",
      groupIdealTitle: "Mô hình lý tưởng (2 học sinh / kit)",
      groupIdealBadge: "Khuyến nghị cao nhất",
      groupIdealDesc: "1 học sinh phụ trách cơ khí & cơ cấu chấp hành, 1 học sinh phụ trách thuật toán & biến trạng thái. Đảm bảo 100% thời lượng cả hai em đều trực tiếp can thiệp và thử nghiệm trên sa bàn.",
      groupOptTitle: "Mô hình tối ưu chi phí (3 học sinh / kit)",
      groupOptBadge: "Lựa chọn linh hoạt",
      groupOptDesc: "Phân vai rõ ràng theo chuẩn đội thi đấu: Kỹ sư cơ cấu (Mechanics), Lập trình viên chính (Coder), và Điều phối viên sa bàn kiêm Chiến thuật viên (Navigator/Strategist).",
      // 8. Final project section
      finalProjTitle: "Dự án cuối khóa: Mini Invention Trials",
      finalProjSub: "Đấu trường mô phỏng hai lượt chạy chính thức và khung đánh giá 4 mức độ.",
      finalProjLead: "Học sinh kết thúc HP6 bằng đấu trường thực nghiệm Mini Invention Trials với 2 lượt chạy tính điểm độc lập, thời gian giới hạn 150 giây và nhiệm vụ bí mật được công bố ngay trước trận đấu.",
      runsTitle: "Cấu trúc 2 lượt chạy tiêu chuẩn:",
      run1Title: "Lượt chạy 1: Nhiệm vụ cơ sở (Autonomous Baseline)",
      run1Desc: "Thực hiện lộ trình điểm số đã chuẩn bị, đánh giá độ ổn định và khả năng căn chỉnh bám biên tự động.",
      run2Title: "Lượt chạy 2: Nhiệm vụ biến thiên (Adaptive Challenge)",
      run2Desc: "Áp dụng biến số nhiệm vụ bí mật (thay đổi vị trí vật phẩm hoặc thứ tự ưu tiên), kiểm tra phản xạ chiến thuật.",
      rubricTitle: "Khung đánh giá Rubric 4 mức độ:",
      rubricLevels: [{
        level: "Mức 1 – Tiếp cận (Beginner)",
        desc: "Robot di chuyển theo sa bàn nhưng chưa hoàn thành trọn vẹn thao tác vật lý; sai số bám biên còn lớn."
      }, {
        level: "Mức 2 – Đạt chuẩn (Proficient)",
        desc: "Hoàn thành các nhiệm vụ cốt lõi, sử dụng cơ cấu chấp hành ổn định, hoàn thành lượt chạy trong 150 giây."
      }, {
        level: "Mức 3 – Tinh thông (Advanced)",
        desc: "Đóng gói hàm nhiệm vụ mượt mà, tối ưu hóa 1 động cơ với cơ cấu bị động, đạt trên 80% điểm số tối đa."
      }, {
        level: "Mức 4 – Xuất sắc (Mastery)",
        desc: "Tỷ lệ lặp lại ổn định >90%, phản xạ tức thì trước nhiệm vụ bí mật, chiến thuật lộ trình tối ưu toàn diện."
      }],
      // 9. Pathway Map
      pathwayTitle: "Vị trí trong lộ trình Robotics",
      pathwayDesc: "HP6 là khóa học chốt (capstone) của tuyến Robotics cơ sở trước khi chuyển tiếp sang phân nhánh thi đấu Enjoy AI.",
      currentTag: "Đang xem",
      // 10. What's next card
      nextStepBadge: "Bước tiếp theo",
      nextStepTitle: "Bước tiếp theo của học viên là gì?",
      nextStepSub: "Sau khi hoàn thành HP6, học viên sẵn sàng tiến vào phân nhánh thi đấu chuyên biệt.",
      nextStepTrackName: "Enjoy AI – Chuẩn bị thi",
      nextStepTrackDesc: "Tuyến huấn luyện thi đấu chuyên biệt tập trung vào kỹ năng chiến thuật nâng cao, cọ xát với các bộ đề thi chuẩn quốc tế và tinh thần thi đấu đồng đội theo quy chế Enjoy AI.",
      nextStepNotice: "Lưu ý quan trọng: Tham gia giai đoạn chuẩn bị thi Enjoy AI là chương trình huấn luyện nâng cao và không đảm bảo kết quả thi đấu, giải thưởng hay chứng nhận tại các kỳ thi chính thức.",
      nextStepAction: "XEM ENJOY AI →",
      // Footer Navigation
      prevBtn: "← HP5 — Thành phố xe tự lái",
      nextBtn: "Khóa luyện thi Enjoy AI →",
      nextSub: "Liên kết tới phân nhánh luyện thi chuyên sâu Enjoy AI"
    },
    en: {
      // 1. Breadcrumb
      breadcrumbHome: "Home",
      breadcrumbPathway: "Robotics Pathway",
      breadcrumbTrack: "Robotics Ages 7+",
      breadcrumbCurrent: "HP6 – Mission Robotics",
      // 2. Hero & Quick Specs
      courseCode: "HP6",
      courseName: "Mission Robotics",
      tagline: "Strategic mechanics, passive execution, and autonomous mission arenas.",
      age: "Ages 7+",
      platform: "WhalesBot AI Module 1S",
      duration: "12 sessions × 90 mins",
      groupSize: "2–3 students / kit",
      prerequisite: "Completion of HP5",
      quote: "Design strategy and solve missions like a Robotics engineer.",
      btnPathway: "VIEW FULL PATHWAY",
      btnConsult: "BOOK CONSULTATION",
      specsTitle: "Course Specifications",
      specsStandardKit: "Standard Hardware Kit:",
      specsKitDesc: "WhalesBot AI Module 1S (AI sensor suite, servo motors, modular structural chassis)",
      // 3. Who is this course for
      whoTitle: "Who is this course for?",
      whoLead: "For learners who completed HP5. HP6 is the simulated arena module, training mission strategy and execution mechanisms.",
      whoPoints: [{
        title: "Students who completed HP5",
        desc: "Learners who have mastered computer vision concepts, road sign recognition, and autonomous navigation from HP5."
      }, {
        title: "Capstone Mission-Strategy Course",
        desc: "The pinnacle of the core 7+ track, integrating mechanical linkages, algorithmic state control, and real-time arena time budgeting."
      }, {
        title: "Transition to separate Enjoy AI track (Explicitly not 'HP7')",
        desc: "Graduates transition directly to the dedicated Enjoy AI competitive coaching track rather than an incremental 'HP7' course level."
      }],
      // 4. 12-session timeline grouped into 6 stages
      timelineTitle: "12-Session Structure (6 Stages)",
      timelineDesc: "The 12-session progression is organized into 6 technical stages built strictly from HP6's official 11 topics.",
      timelineStages: [{
        stageNum: "01",
        stageLabel: "Stage 1",
        sessions: "Sessions 1 – 2",
        title: "Arena Standards & Mission Mechanisms",
        topicsCovered: "Mission Mechanisms",
        desc: "Study the arena mat layout, analyze rulebooks and scoring points, and engineer custom end-effectors for physical obstacle interaction."
      }, {
        stageNum: "02",
        stageLabel: "Stage 2",
        sessions: "Sessions 3 – 4",
        title: "Single-Motor Output & 3D Object Handling",
        topicsCovered: "One Motor + Passive Mechanisms & Push / Lift / Drop / Launch",
        desc: "Harness gravity, linkages, and ratchets to double single-motor outputs; master the four core physical actions to manipulate game objects."
      }, {
        stageNum: "03",
        stageLabel: "Stage 3",
        sessions: "Sessions 5 – 6",
        title: "Arena Alignment & Mission Functions",
        topicsCovered: "Alignment & Mission Functions",
        desc: "Master wall-squaring and boundary calibration to reset coordinates and eliminate drift; package action sequences into modular functions."
      }, {
        stageNum: "04",
        stageLabel: "Stage 4",
        sessions: "Sessions 7 – 8",
        title: "State Variables & Strategic Routing",
        topicsCovered: "State Variables & Strategic Routing",
        desc: "Manage dynamic logical flags to track step memory; budget the 150-second clock and plan optimal waypoints for high-value targets."
      }, {
        stageNum: "05",
        stageLabel: "Stage 5",
        sessions: "Sessions 9 – 10",
        title: "150-second Clock & Repeatability",
        topicsCovered: "150-second Mission & Repeatability",
        desc: "Execute complete autonomous routines under strict 150-second limits; validate run variance across dozens of controlled trial runs."
      }, {
        stageNum: "06",
        stageLabel: "Stage 6",
        sessions: "Sessions 11 – 12",
        title: "Secret Mission Drill & Simulated Tournament",
        topicsCovered: "Secret Mission Drill & Simulated Tournament",
        desc: "Drill rapid engineering reflexes when unexpected obstacle parameters are introduced; manage tournament pressure and defend tactics."
      }],
      // 11 Core Topics
      topicsHeading: "11 Engineering Competencies of HP6",
      topicsSub: "Engineered for intelligent mechanical advantage, single motor with passive gear systems, and 150-second autonomous runs.",
      topics: [{
        title: "Mission Mechanisms",
        desc: "Custom end-effectors designed for distinct arena obstacles and mechanical tasks.",
        icon: Wrench
      }, {
        title: "One Motor + Passive Mechanisms",
        desc: "Leveraging gravity, ratchets, and clever kinematic linkages to multiply single-motor outputs.",
        icon: Cpu
      }, {
        title: "Push, Lift, Drop, Launch",
        desc: "Mastering the four foundational physical actions to manipulate game objects reliably.",
        icon: Layers
      }, {
        title: "Alignment",
        desc: "Wall-squaring and sensor datum calibration to eliminate cumulative dead-reckoning errors.",
        icon: Target
      }, {
        title: "Mission Functions",
        desc: "Modular coding blocks packaging mission sequences for crisp, reusable architecture.",
        icon: Bot
      }, {
        title: "State Variables",
        desc: "Dynamic condition tracking and step memory to govern sequential decision branches.",
        icon: Sparkles
      }, {
        title: "Strategic Routing",
        desc: "High-value point prioritization, time budgets, and optimized arena waypoints.",
        icon: Flag
      }, {
        title: "150-second Mission",
        desc: "Fully autonomous execution solving complex score missions within strict 150-second limit.",
        icon: Timer
      }, {
        title: "Repeatability",
        desc: "Statistical run validation across multiple consecutive trials to minimize variance.",
        icon: CheckCircle2
      }, {
        title: "Secret Mission Drill",
        desc: "Fast technical adaptation when surprise elements are introduced shortly before launch.",
        icon: ShieldAlert
      }, {
        title: "Simulated Tournament",
        desc: "Real-time match pressure, pitch-side troubleshooting, and coordinated team collaboration.",
        icon: RotateCcw
      }],
      // Format Note Alert
      noticeTitle: "Format Note & Enjoy AI Clarification",
      noticeBody: "This stage adopts a simulated Invention Trials tournament format with 150-second autonomous runs to cultivate competitive engineering mindset. This is an internal simulation, not the official Enjoy AI exam itself, and completing HP6 does not guarantee tournament qualification.",
      // 5. 4-step Learning Method
      methodTitle: "4-Step Learning Methodology",
      methodDesc: "A disciplined engineering cycle applied during every session to systematically solve autonomous arena challenges.",
      methodSteps: [{
        step: "01",
        name: "Warm-up & Mission Brief",
        desc: "Analyze the arena mission objectives, study competition rules, establish scoring priorities, and calculate time constraints.",
        icon: Play
      }, {
        step: "02",
        name: "Assembly & Mechanical Rigging",
        desc: "Build task end-effectors (push, lift, drop, launch) optimizing passive linkages and single-motor leverage.",
        icon: Wrench
      }, {
        step: "03",
        name: "Strategic Programming & Alignment",
        desc: "Develop reusable mission functions, manage state variables, calibrate wall alignment, and optimize autonomous paths.",
        icon: Target
      }, {
        step: "04",
        name: "Presentation / Match Simulation",
        desc: "Execute autonomous trial runs under competitive match pressure, validate variance, and reflect on adjustments.",
        icon: Presentation
      }],
      // 6. Equipment Card
      equipTitle: "Equipment & Hardware Requirements",
      equipSubtitle: "Standard technical equipment specification for module HP6",
      equipKitLabel: "Hardware Kit:",
      equipKitName: "WhalesBot AI Module 1S",
      equipKitDesc: "Integrated AI processing controller, computer vision sensor suite, precision servo motors, and mechanical linkage parts.",
      equipCostLabel: "Reference equipment cost (not tuition):",
      equipCostValue: "~6,500,000 VNĐ / kit",
      equipComputerLabel: "Computer Requirement:",
      equipComputerValue: "Computer required (Laptop) for advanced block scripting and compiling autonomous mission routines.",
      equipNote: "Note: The kit cost above is the reference market hardware price and does not constitute tuition fees.",
      // 7. Small-group learning model
      groupModelTitle: "Small-Group Learning Model",
      groupModelLead: "Maximizing hands-on engineering time and authentic team tactical coordination on the arena mat.",
      groupIdealTitle: "Ideal Model (2 students / kit)",
      groupIdealBadge: "Top Recommended",
      groupIdealDesc: "1 student focuses on mechanics & end-effectors, 1 student focuses on algorithmic logic & state variables. Ensures 100% active engagement and mat testing for both students throughout the 90-minute session.",
      groupOptTitle: "Cost-Optimized Model (3 students / kit)",
      groupOptBadge: "Flexible Option",
      groupOptDesc: "Structured tournament team roles: Mechanical Rigging Lead, Primary Algorithmic Programmer, and Mat Coordinator / Tactical Strategist.",
      // 8. Final project section
      finalProjTitle: "Final Project: Mini Invention Trials",
      finalProjSub: "Simulated arena competition featuring two official-style runs and a 4-level evaluation rubric.",
      finalProjLead: "HP6 concludes with the Mini Invention Trials capstone arena: two independent timed runs, a strict 150-second clock, and surprise secret mission variables announced right before match time.",
      runsTitle: "Two Official-Style Match Runs:",
      run1Title: "Run 1: Autonomous Baseline Mission",
      run1Desc: "Execute the planned high-value waypoint sequence, evaluating baseline mechanism stability and wall alignment.",
      run2Title: "Run 2: Adaptive Challenge Run",
      run2Desc: "Integrate surprise secret mission parameters (shifted item positions or modified scoring priority) to test real-time adaptation.",
      rubricTitle: "4-Level Competency Rubric:",
      rubricLevels: [{
        level: "Level 1 – Beginner",
        desc: "Robot navigates the arena but struggles with physical item manipulation; significant drift accumulation."
      }, {
        level: "Level 2 – Proficient",
        desc: "Executes core mission objectives reliably using stable end-effectors; completes full run within 150 seconds."
      }, {
        level: "Level 3 – Advanced",
        desc: "Clean modular mission functions, effective single-motor passive leverage, securing over 80% of target points."
      }, {
        level: "Level 4 – Mastery",
        desc: "Run repeatability exceeding 90%, immediate adaptation to secret mission drills, and optimal route tactical efficiency."
      }],
      // 9. Pathway Map
      pathwayTitle: "Position in Robotics Pathway",
      pathwayDesc: "HP6 is the core capstone course before students transition into the separate Enjoy AI competitive tournament track.",
      currentTag: "Current",
      // 10. What's next card
      nextStepBadge: "Next Step",
      nextStepTitle: "What's next for your child?",
      nextStepSub: "After completing HP6, students are prepared to transition into competitive arena robotics.",
      nextStepTrackName: "Enjoy AI – Exam Preparation",
      nextStepTrackDesc: "An intensive competitive program focusing on advanced game strategy, official international mission sets, and collaborative team tactics under official Enjoy AI regulations.",
      nextStepNotice: "Important Note: Entering the Enjoy AI preparation stage is an intensive training pathway and does not guarantee tournament results, awards, or official qualification.",
      nextStepAction: "VIEW ENJOY AI →",
      // Footer Navigation
      prevBtn: "← HP5 — Autonomous City",
      nextBtn: "Enjoy AI Intensive Pathway →",
      nextSub: "Advance to dedicated competitive training track"
    }
  };
  const cur = t[lang];

  // 10-node pathway list
  const pathwayNodes = [{
    code: "D1",
    name: "Duplo 1",
    path: "/RoboticsCourseD1Page",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-sizes"
  }, {
    code: "D2",
    name: "Duplo 2",
    path: "/RoboticsCourseD2Page",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-sizes"
  }, {
    code: "D3",
    name: "Duplo 3",
    path: "/RoboticsCourseD3Page",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-sizes"
  }, {
    code: "D4",
    name: "Duplo 4",
    path: "/RoboticsCourseD4Page",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-sizes"
  }, {
    code: "HP1",
    name: "WhalesBot 1",
    path: "/RoboticsCourseHP1Page",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-sizes"
  }, {
    code: "HP2",
    name: "WhalesBot 2",
    path: "/RoboticsCourseHP2Page",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-sizes"
  }, {
    code: "HP3",
    name: "WhalesBot 3",
    path: "/RoboticsCourseHP3Page",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-sizes"
  }, {
    code: "HP4",
    name: "WhalesBot 4",
    path: "/RoboticsCourseHP4Page",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-sizes"
  }, {
    code: "HP5",
    name: "WhalesBot 5",
    path: "/RoboticsCourseHp5Page",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-sizes"
  }, {
    code: "HP6",
    name: "Robot nhiệm vụ",
    path: "/RoboticsCourseHP6Page",
    isCurrent: true,
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-sizes"
  }];
  return <section id="roboticscoursehp6detail" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <PostsProvider wp_query={{
      post_type: "program",
      per_page: 3,
      status: ["publish"]
    }}>
        <ProgramContextConsumer />
      </PostsProvider>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* (1) Full 4-level breadcrumb + Language Switcher */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="font-medium text-muted-foreground hover:text-foreground transition-colors">
                    {cur.breadcrumbHome}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/RoboticsLearningPathwayPage" className="font-medium text-muted-foreground hover:text-foreground transition-colors">
                    {cur.breadcrumbPathway}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="#" className="font-medium text-muted-foreground hover:text-foreground transition-colors">
                    {cur.breadcrumbTrack}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-semibold text-primary">
                  {cur.breadcrumbCurrent}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="text-xs uppercase font-medium text-muted-foreground tracking-wider">
              Language:
            </span>
            <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/60">
              <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" className="h-7 px-3 text-xs font-semibold" onClick={() => setLang("vi")}>
                Tiếng Việt
              </Button>
              <Button variant={lang === "en" ? "default" : "ghost"} size="sm" className="h-7 px-3 text-xs font-semibold" onClick={() => setLang("en")}>
                English
              </Button>
            </div>
          </div>
        </div>

        {/* (2) Hero Section with Dual CTA & Hero Technical Visual */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 space-y-6">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="default" className="text-sm px-3 py-1 font-bold">
                {cur.courseCode}
              </Badge>
              <Badge variant="outline" className="text-xs px-2.5 py-0.5 border-border">
                {cur.platform}
              </Badge>
              <Badge variant="secondary" className="text-xs px-2.5 py-0.5">
                {cur.prerequisite}
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-serif">
              {cur.courseCode}: {cur.courseName}
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl">
              {cur.tagline}
            </p>

            {/* Concept Quote */}
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 md:p-6 flex items-start gap-4">
              <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Quote className="size-5" />
              </div>
              <div className="space-y-1">
                <p className="text-base sm:text-lg font-semibold text-foreground italic">
                  "{cur.quote}"
                </p>
                <p className="text-xs uppercase tracking-wider font-semibold text-primary">
                  ROBOTO Academy Standard
                </p>
              </div>
            </div>

            {/* Top Hero Visual Card: Mission Robot with End-Effector Mechanism */}
            <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-xs group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_hp6_mission_robot.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_hp6_mission_robot.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_hp6_mission_robot.png-wvc-sizes" alt="Mission-capable autonomous robot equipped with multi-purpose push, lift, drop, and launch mechanism attachments" loading="lazy" className="w-full h-64 sm:h-80 md:h-96 object-cover object-center group-hover:scale-[1.015] transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent pointer-events-none" />
              <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                <Badge variant="default" className="bg-primary/90 text-white font-mono text-[11px] backdrop-blur-xs">
                  HP6 MISSION RIG • PUSH / LIFT / DROP / LAUNCH MECHANISM
                </Badge>
                <span className="text-foreground/90 font-semibold bg-background/85 backdrop-blur-xs px-2.5 py-1 rounded-md border border-border text-[11px]">
                  WhalesBot AI Module 1S
                </span>
              </div>
            </div>

            {/* Primary & Secondary Hero CTAs */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-white font-semibold gap-2">
                <Link to="/RoboticsLearningPathwayPage">
                  <Compass className="size-4" />
                  {cur.btnPathway}
                </Link>
              </Button>
              <Button asChild variant="default" size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm font-semibold gap-2">
                <Link to="/FreeTrialContactPage">
                  <Sparkles className="size-4" />
                  {cur.btnConsult}
                </Link>
              </Button>
            </div>
          </div>

          {/* Quick Specifications Card */}
          <div className="lg:col-span-4">
            <Card className="border-border shadow-md bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-bold flex items-center gap-2 text-foreground font-serif">
                  <Bot className="size-5 text-primary" />
                  {cur.specsTitle}
                </CardTitle>
                <CardDescription className="text-xs">
                  {cur.platform}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Users className="size-4 text-muted-foreground" />
                    {lang === "vi" ? "Độ tuổi" : "Age Range"}
                  </span>
                  <span className="font-semibold text-foreground">{cur.age}</span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Clock className="size-4 text-muted-foreground" />
                    {lang === "vi" ? "Thời lượng" : "Duration"}
                  </span>
                  <span className="font-semibold text-foreground">{cur.duration}</span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Users className="size-4 text-muted-foreground" />
                    {lang === "vi" ? "Quy mô nhóm" : "Group Size"}
                  </span>
                  <span className="font-semibold text-foreground">{cur.groupSize}</span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-muted-foreground" />
                    {lang === "vi" ? "Điều kiện tiên quyết" : "Prerequisite"}
                  </span>
                  <span className="font-semibold text-primary">{cur.prerequisite}</span>
                </div>

                <div className="pt-2">
                  <div className="rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground leading-relaxed">
                    <span className="font-semibold text-foreground block mb-1">
                      {cur.specsStandardKit}
                    </span>
                    {cur.specsKitDesc}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* (3) 'Who is this course for?' section */}
        <div className="space-y-6">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
              {cur.whoTitle}
            </h2>
            <p className="mt-2 text-base text-muted-foreground leading-relaxed">
              {cur.whoLead}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cur.whoPoints.map((item, idx) => <Card key={idx} data-index={idx} className="border-border bg-card shadow-xs rounded-xl hover:border-primary/40 transition-colors">
                <CardHeader className="pb-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-2 font-bold text-sm">
                    {idx + 1}
                  </div>
                  <CardTitle className="text-base sm:text-lg font-bold text-foreground font-serif leading-snug">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* (4) 12-session timeline grouped into 6 stages */}
        <div className="space-y-6">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
              {cur.timelineTitle}
            </h2>
            <p className="mt-2 text-base text-muted-foreground leading-relaxed">
              {cur.timelineDesc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {cur.timelineStages.map((stg, idx) => <Card key={idx} data-index={idx} className="border-border bg-card hover:border-primary/40 transition-colors shadow-xs flex flex-col justify-between">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-primary">
                      {stg.stageLabel}
                    </span>
                    <Badge variant="outline" className="text-[11px] font-semibold border-border">
                      {stg.sessions}
                    </Badge>
                  </div>
                  <CardTitle className="text-base sm:text-lg font-bold text-foreground font-serif leading-snug">
                    {stg.title}
                  </CardTitle>
                  <CardDescription className="text-xs text-primary font-medium mt-1">
                    {stg.topicsCovered}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {stg.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Format-Note Alert: Simulated Tournament & Enjoy AI Clarification */}
        <Alert className="border-accent/40 bg-accent/10 text-foreground py-5 px-6 rounded-xl">
          <AlertCircle className="size-6 text-accent mt-0.5 shrink-0" />
          <div className="ml-3 space-y-1">
            <AlertTitle className="text-base font-bold text-foreground font-serif">
              {cur.noticeTitle}
            </AlertTitle>
            <AlertDescription className="text-sm text-muted-foreground leading-relaxed">
              {cur.noticeBody}
            </AlertDescription>
          </div>
        </Alert>

        {/* 11 Official HP6 Topics Focus */}
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
              {cur.topicsHeading}
            </h2>
            <p className="mt-2 text-base text-muted-foreground max-w-3xl leading-relaxed">
              {cur.topicsSub}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {cur.topics.map((item, idx) => {
            const IconComp = item.icon;
            return <Card key={idx} data-index={idx} className="border-border bg-card hover:border-primary/40 transition-colors shadow-xs">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <IconComp className="size-5" />
                      </div>
                      <CardTitle className="text-base font-bold text-foreground leading-tight font-serif">
                        {item.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Mid-Page Visual Feature Banner: Mission Strategy & Arena Testing Scene */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch">
          <div className="lg:col-span-7 relative min-h-[280px] sm:min-h-[340px]">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_hp6_task_mat_strategy.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_hp6_task_mat_strategy.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_hp6_task_mat_strategy.png-wvc-sizes" alt="Students testing robotics mission strategy, wall alignment, and 150-second waypoint routes on standard arena task mat" loading="lazy" className="w-full h-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent lg:hidden" />
          </div>
          <div className="lg:col-span-5 p-6 sm:p-8 space-y-4 flex flex-col justify-center">
            <Badge variant="outline" className="border-primary text-primary text-xs font-bold uppercase tracking-wider w-fit">
              {lang === "vi" ? "Thực chiến sa bàn" : "Task Mat Strategy"}
            </Badge>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-serif">
              {lang === "vi" ? "Đấu trường thử nghiệm & Tối ưu lộ trình 150s" : "Arena Testing & 150s Route Optimization"}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {lang === "vi" ? "Học sinh trực tiếp thực thi chiến thuật bám biên, hiệu chuẩn tọa độ và đo lường độ lặp lại trên sa bàn tiêu chuẩn trước khi tham gia đấu trường mô phỏng." : "Students calibrate wall alignment, measure run repeatability, and refine high-value waypoint strategy directly on official arena task mats."}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge variant="secondary" className="text-xs font-semibold">
                {lang === "vi" ? "Định vị Alignment" : "Alignment Calibration"}
              </Badge>
              <Badge variant="secondary" className="text-xs font-semibold">
                {lang === "vi" ? "Quản lý 150s" : "150s Time Budget"}
              </Badge>
            </div>
          </div>
        </div>

        {/* (5) 4-step Learning Method */}
        <div className="space-y-6">
          <div className="max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-serif">
              {cur.methodTitle}
            </h2>
            <p className="mt-2 text-base text-muted-foreground leading-relaxed">
              {cur.methodDesc}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cur.methodSteps.map((step, idx) => {
            const IconComp = step.icon;
            return <div key={idx} data-index={idx} className="rounded-xl border border-border bg-card p-6 shadow-xs flex flex-col justify-between hover:border-secondary transition-all">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-secondary uppercase tracking-widest">
                        BƯỚC {step.step}
                      </span>
                      <div className="size-10 rounded-lg bg-secondary/15 text-secondary flex items-center justify-center">
                        <IconComp className="size-5" />
                      </div>
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-foreground font-serif">
                      {step.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* (6) Equipment Card & (7) Small-Group Learning Model */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Equipment Card */}
          <div className="lg:col-span-6">
            <Card className="border-border bg-card shadow-xs h-full flex flex-col justify-between overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Laptop className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg sm:text-xl font-bold text-foreground font-serif">
                      {cur.equipTitle}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {cur.equipSubtitle}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="rounded-lg border border-border overflow-hidden bg-muted/20">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-sizes" alt="WhalesBot AI Module 1S robotics kit used in ROBOTO's HP1–HP6 engineering track" loading="lazy" className="w-full h-40 sm:h-48 object-cover object-center" />
                </div>

                <div className="rounded-lg border border-border p-3.5 bg-muted/40 space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-primary block">
                    {cur.equipKitLabel}
                  </span>
                  <p className="font-bold text-foreground text-base font-serif">
                    {cur.equipKitName}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {cur.equipKitDesc}
                  </p>
                </div>

                <div className="rounded-lg border border-primary/20 p-3.5 bg-primary/5 space-y-1">
                  <span className="text-xs font-semibold text-muted-foreground block">
                    {cur.equipCostLabel}
                  </span>
                  <p className="text-lg font-extrabold text-primary font-serif">
                    {cur.equipCostValue}
                  </p>
                </div>

                <div className="rounded-lg border border-border p-3.5 bg-muted/40 space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-secondary block">
                    {cur.equipComputerLabel}
                  </span>
                  <p className="text-xs text-foreground font-medium leading-relaxed">
                    {cur.equipComputerValue}
                  </p>
                </div>

                <p className="text-xs text-muted-foreground italic leading-relaxed pt-1">
                  {cur.equipNote}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Small-Group Learning Model */}
          <div className="lg:col-span-6">
            <Card className="border-border bg-card shadow-xs h-full flex flex-col justify-between">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-secondary/15 text-secondary flex items-center justify-center">
                    <Users className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg sm:text-xl font-bold text-foreground font-serif">
                      {cur.groupModelTitle}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {cur.groupModelLead}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="rounded-xl border border-secondary/40 bg-secondary/5 p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-foreground text-sm font-serif">
                      {cur.groupIdealTitle}
                    </h3>
                    <Badge variant="secondary" className="text-[10px] font-bold">
                      {cur.groupIdealBadge}
                    </Badge>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {cur.groupIdealDesc}
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-foreground text-sm font-serif">
                      {cur.groupOptTitle}
                    </h3>
                    <Badge variant="outline" className="text-[10px] font-semibold border-border">
                      {cur.groupOptBadge}
                    </Badge>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {cur.groupOptDesc}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* (8) Final Project Section: Mini Invention Trials */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-8 shadow-xs">
          <div className="max-w-3xl space-y-2">
            <Badge variant="default" className="text-xs font-bold uppercase tracking-wider">
              {lang === "vi" ? "Dự án tốt nghiệp" : "Capstone Final Project"}
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
              {cur.finalProjTitle}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {cur.finalProjLead}
            </p>
          </div>

          {/* Two Official-Style Runs */}
          <div className="space-y-4">
            <h3 className="text-base font-bold text-foreground font-serif flex items-center gap-2">
              <Timer className="size-5 text-primary" />
              {cur.runsTitle}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border p-5 bg-muted/40 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="size-6 rounded-md bg-primary text-white flex items-center justify-center font-bold text-xs">
                    1
                  </span>
                  <h4 className="font-bold text-foreground text-sm font-serif">
                    {cur.run1Title}
                  </h4>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {cur.run1Desc}
                </p>
              </div>

              <div className="rounded-xl border border-border p-5 bg-muted/40 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="size-6 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center font-bold text-xs">
                    2
                  </span>
                  <h4 className="font-bold text-foreground text-sm font-serif">
                    {cur.run2Title}
                  </h4>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {cur.run2Desc}
                </p>
              </div>
            </div>
          </div>

          {/* 4-Level Rubric */}
          <div className="space-y-4">
            <h3 className="text-base font-bold text-foreground font-serif flex items-center gap-2">
              <Award className="size-5 text-primary" />
              {cur.rubricTitle}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {cur.rubricLevels.map((rb, idx) => <div key={idx} data-index={idx} className="rounded-xl border border-border p-4 bg-background flex flex-col justify-between space-y-2">
                  <span className="text-xs font-bold text-primary block uppercase tracking-wider">
                    {rb.level}
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {rb.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* (9) 10-node pathway map with HP6 highlighted */}
        <div className="p-6 md:p-8 rounded-xl border border-border bg-card shadow-xs space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground font-serif flex items-center gap-2">
                <Compass className="size-5 text-primary" />
                {cur.pathwayTitle}
              </h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                {cur.pathwayDesc}
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="w-fit border-border hover:bg-muted">
              <Link to="/RoboticsLearningPathwayPage">
                {cur.btnPathway} →
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2.5 pt-2">
            {pathwayNodes.map((node, idx) => {
            if (node.isCurrent) {
              return <div key={node.code} data-index={idx} className="p-3 rounded-lg border-2 border-primary bg-primary/10 text-center flex flex-col justify-between items-center transition-all shadow-xs">
                    <img src={node.src} data-wvc-srcset={node.srcset} data-wvc-sizes={node.sizes} alt={`${node.code} robotics course thumbnail`} loading="lazy" className="size-11 object-contain mb-1.5 rounded-md" />
                    <span className="text-xs uppercase font-extrabold text-primary tracking-wide">
                      {node.code}
                    </span>
                    <span className="text-xs font-semibold text-foreground mt-1">
                      {node.name}
                    </span>
                    <Badge variant="default" className="text-[10px] py-0 px-1.5 mt-2 bg-primary">
                      {cur.currentTag}
                    </Badge>
                  </div>;
            }
            return <Link key={node.code} to={node.path || "/RoboticsLearningPathwayPage"} data-index={idx} className="p-3 rounded-lg border border-border bg-muted/40 hover:bg-muted hover:border-primary/50 text-center flex flex-col justify-between items-center transition-all group">
                  <img src={node.src} data-wvc-srcset={node.srcset} data-wvc-sizes={node.sizes} alt={`${node.code} robotics course thumbnail`} loading="lazy" className="size-11 object-contain mb-1.5 rounded-md group-hover:scale-105 transition-transform" />
                  <span className="text-xs uppercase font-bold text-muted-foreground group-hover:text-primary transition-colors">
                    {node.code}
                  </span>
                  <span className="text-xs font-medium text-foreground mt-1 line-clamp-1">
                    {node.name}
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-2 group-hover:text-primary">
                    Chi tiết →
                  </span>
                </Link>;
          })}
          </div>
        </div>

        {/* (10) 'What's next' card: Enjoy AI – Chuẩn bị thi */}
        <div className="rounded-xl border border-secondary/40 bg-secondary/5 p-6 md:p-8 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1">
              <Badge variant="secondary" className="text-xs font-bold uppercase tracking-wider mb-1">
                {cur.nextStepBadge}
              </Badge>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground font-serif">
                {cur.nextStepTitle}
              </h2>
              <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
                {cur.nextStepSub}
              </p>
            </div>
            <Button asChild variant="default" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm shrink-0 font-semibold gap-2">
              <Link to="#">
                {cur.nextStepAction}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="rounded-lg border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className="size-9 rounded-lg bg-secondary/20 text-secondary flex items-center justify-center font-bold">
                <Trophy className="size-5" />
              </div>
              <h3 className="text-base font-bold text-foreground font-serif">
                {cur.nextStepTrackName}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {cur.nextStepTrackDesc}
            </p>
            <div className="text-xs text-muted-foreground bg-muted/60 p-3 rounded-md flex items-start gap-2">
              <AlertCircle className="size-4 text-muted-foreground shrink-0 mt-0.5" />
              <span>{cur.nextStepNotice}</span>
            </div>
          </div>
        </div>

        {/* Previous Course & Next Pathway Navigation */}
        <div className="pt-6 border-t border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="w-full md:w-auto">
            <Button asChild variant="outline" size="lg" className="w-full md:w-auto border-border hover:bg-muted text-foreground justify-center gap-2">
              <Link to="/RoboticsCourseHp5Page">
                <ArrowLeft className="size-4" />
                {cur.prevBtn}
              </Link>
            </Button>
          </div>

          <div className="w-full md:w-auto flex flex-col sm:flex-row items-center gap-3">
            <div className="text-center sm:text-right hidden sm:block">
              <span className="text-xs text-muted-foreground block">
                {cur.nextSub}
              </span>
            </div>
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm justify-center gap-2 font-semibold">
              <Link to="#">
                {cur.nextBtn}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}